import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { OrderingConstants, OrderingOperations, OrderingSelectors } from 'polygon-ordering';
import { RedcatPaymentCardForm } from './RedcatPaymentCardForm';
import { RedcatPaymentCardFormStyle } from './RedcatPaymentCardFormStyle';
import { useRedcatPayment } from '../hooks/useRedcatPayment';
import { isPayButtonDisabled } from '../utils/isPayButtonDisabled';
import { enqueueErrorSnackbar } from '../../../utils/snackbar';
import history from '../../../history';
import { PAYMENT_SESSION_STATUS } from '../../../libs/polygon-ordering/src/constants/paymentSessionStatus';
import StandardButton from '../../../components/StandardButton';
import TouchableOpacity from '../../../components/TouchableOpacity';
import Text from '../../../components/Text';
import { Show } from '../../../components/atoms/Show';
import RedcatPaySavedCard from '../../../components/RedcatPaySavedCard';
import { setCurrentModal } from '../../../thunks/setCurrentModal';
import { MESSAGE_MODAL_ID } from '../../../modals/MessageModal';
import getThemeLookup from '../../../selectors/getThemeLookup';
import combineStyles from '../../../utils/combineStyles';
import { RiCheckboxBlankCircleLine, RiLockLine, RiRecordCircleFill } from 'react-icons/ri';
import { trackPaymentFailed, trackPaymentInitiated, useRedcatAnalytics } from '../../../analytics';

const { getGatewayMode, getMember, getPaymentGatewayTokens } = OrderingSelectors;
const { chargeRedcatPaySavedCard } = OrderingOperations;
const { PAYMENT_METHOD } = OrderingConstants;

interface RedcatPaySavedCardPaymentFormProps {
  readonly amount: number;
  readonly loyalty: Record<string, unknown>;
  readonly receiptEmail?: string;
  readonly buttonLabel: string;
  readonly failureMessage: string;
  readonly goBackOnNewCardError?: boolean;
  readonly onFinalise: (reference: string) => Promise<void>;
}

// Shared by GiftCardPurchaseRedcatPay, GiftCardTopUpRedcatPay, and MemberMoneyRedcatPayTopUp —
// each of those callers only differs in the loyalty payload shape and how they finalise the
// sale (which thunk to dispatch, what route to navigate to, how to reset their own form).
const RedcatPaySavedCardPaymentForm = ({
  amount,
  loyalty,
  receiptEmail,
  buttonLabel,
  failureMessage,
  goBackOnNewCardError = false,
  onFinalise,
}: RedcatPaySavedCardPaymentFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const p = useAppSelector(getThemeLookup);
  const gatewayMode = useAppSelector(getGatewayMode);
  const apiHandlerReady = useAppSelector((state: any) => state.apiHandlerReady);
  const giftCardTopUpLocationId = useAppSelector(
    (state: any) => state.config.giftCardTopUpLocationId,
  );

  const member = useAppSelector(getMember);
  const memberRef = member ? { id: Number(member.id), number: member.memberNumber } : undefined;
  const savedCards = useAppSelector(getPaymentGatewayTokens);
  const [selectedSavedCardToken, setSelectedSavedCardToken] = useState<string | null>(null);
  const [creditCardSelected, setCreditCardSelected] = useState(false);
  const [savedCardChargeInProgress, setSavedCardChargeInProgress] = useState(false);

  const {
    submitRedcatPay,
    cardReady,
    cardValid,
    pollingReference,
    setPollingReference,
    idempotencyKey,
    paymentStatus,
    paymentTimedOut,
    resumePaymentStatusPolling,
    paymentInProgress,
    setPaymentInProgress,
    resetPaymentAttempt,
    recaptchaToken,
    setRecaptchaToken,
    requireRecaptcha,
    recaptchaSiteKey,
    purchaseType,
  } = useRedcatPayment();

  const { track } = useRedcatAnalytics();
  const analyticsBase = { purchaseType, memberId: member?.id };

  useEffect(() => {
    if (paymentStatus === PAYMENT_SESSION_STATUS.PAYMENT_SUCCESS && pollingReference) {
      onFinalise(pollingReference).finally(() => {
        resetPaymentAttempt();
        setSavedCardChargeInProgress(false);
      });
    } else if (paymentStatus === PAYMENT_SESSION_STATUS.PAYMENT_FAILED) {
      enqueueErrorSnackbar(failureMessage);
      resetPaymentAttempt();
      setSavedCardChargeInProgress(false);
    }
  }, [paymentStatus]);

  useEffect(() => {
    if (paymentTimedOut) {
      // Timeout ≠ confirmed failure — the charge may have succeeded server-side after we
      // stopped polling. Keep the session/idempotencyKey alive so retrying resumes polling
      // on the same reference instead of firing a brand-new charge. The modal has no close
      // button, so the consumer can't change amount or payment method while the original
      // payment is unresolved — Retry is the only way out.
      const usingSavedCardAtTimeout = savedCardChargeInProgress;
      setSavedCardChargeInProgress(false);
      setPaymentInProgress(false);
      dispatch(
        setCurrentModal({
          modalId: MESSAGE_MODAL_ID,
          params: {
            message: t('paymentTimeoutMessage'),
            hideCloseButton: true,
            alertButtons: [
              {
                text: t('button.retry'),
                isPreferred: true,
                action: () => {
                  dispatch(setCurrentModal(null));
                  if (usingSavedCardAtTimeout) {
                    setSavedCardChargeInProgress(true);
                  } else {
                    setPaymentInProgress(true);
                  }
                  resumePaymentStatusPolling();
                },
              },
            ],
          },
        }),
      );
    }
  }, [paymentTimedOut]);

  const payWithSavedCard = async () => {
    if (pollingReference) {
      resumePaymentStatusPolling();
      return;
    }

    if (!selectedSavedCardToken) return;

    setSavedCardChargeInProgress(true);
    trackPaymentInitiated(track, analyticsBase);
    try {
      const { receipt_id } = await dispatch(
        chargeRedcatPaySavedCard({
          cardToken: selectedSavedCardToken,
          amount,
          storeId: giftCardTopUpLocationId ?? undefined,
          receiptEmail: receiptEmail || undefined,
          loyalty,
          recaptchaToken,
          channel: 'web',
          idempotencyKey,
        }),
      ).unwrap();
      // receipt_id is the same session reference used by the new-card flow — the
      // payment session still needs the Adyen webhook to confirm it before
      // /sales?mode=loyalty will accept it, so poll rather than finalise immediately.
      setPollingReference(receipt_id);
    } catch {
      trackPaymentFailed(track, { ...analyticsBase, failureReason: 'charge_request_failed' });
      enqueueErrorSnackbar(t('paymentFailedMessage'));
      if (requireRecaptcha) setRecaptchaToken(null);
      setSavedCardChargeInProgress(false);
    }
  };

  const usingSavedCard = Boolean(selectedSavedCardToken);
  const hasSavedCards = Boolean(member) && savedCards.length > 0;
  const showCreditCardForm = creditCardSelected || !hasSavedCards;
  const shouldShowRecaptcha =
    usingSavedCard && requireRecaptcha && !recaptchaToken && Boolean(recaptchaSiteKey);
  // Mirrors how checkoutInProgress locks the payment methods on the order screen — once a charge
  // is in flight the consumer must not be able to switch card, or they could end up paying twice.
  const paymentLocked = savedCardChargeInProgress || Boolean(paymentInProgress);

  return (
    <div>
      <Show when={hasSavedCards}>
        <>
          {savedCards.map((savedCard: PaymentGateWayToken) => (
            <RedcatPaySavedCard
              key={savedCard.token}
              savedCard={savedCard}
              selected={selectedSavedCardToken === savedCard.token}
              disabled={paymentLocked}
              onSelect={() => {
                setSelectedSavedCardToken(savedCard.token);
                setCreditCardSelected(false);
              }}
              onRemoved={() => {
                if (selectedSavedCardToken === savedCard.token) {
                  setSelectedSavedCardToken(null);
                }
              }}
            />
          ))}
          <TouchableOpacity
            style={styles.mainContainer}
            disabled={paymentLocked}
            disabledStyle={styles.disabled}
            onClick={() => {
              setSelectedSavedCardToken(null);
              setCreditCardSelected(true);
            }}
          >
            <div style={styles.rightContainer}>
              <div style={styles.topContainer}>
                <Show when={showCreditCardForm}>
                  <RiRecordCircleFill
                    style={combineStyles(p('paymentMethodIcon', ['color', 'fontSize']))}
                  />
                </Show>
                <Show when={!showCreditCardForm}>
                  <RiCheckboxBlankCircleLine
                    style={combineStyles(p('paymentMethodIcon', ['color', 'fontSize']))}
                  />
                </Show>

                <Text themeKey="paymentMethod" style={{ marginRight: 'auto', paddingLeft: 3 }}>
                  {t(`paymentMethod.${PAYMENT_METHOD.CREDIT_CARD}`)}
                </Text>
                <div>
                  <RiLockLine
                    style={{ fontWeight: 'bolder', marginRight: 4 }}
                    size={18}
                    color="#737373"
                  />
                  <Text style={{ fontSize: 14, color: '#737373' }} value={t('securePayment')} />
                </div>
              </div>
            </div>
          </TouchableOpacity>
        </>
      </Show>

      <Show when={showCreditCardForm}>
        <RedcatPaymentCardFormStyle>
          <RedcatPaymentCardForm
            amount={amount}
            storeId={giftCardTopUpLocationId ?? ''}
            environment={gatewayMode === 'production' ? 'live' : 'test'}
            apiHandlerReady={apiHandlerReady}
            disabled={paymentLocked}
            memberRef={memberRef}
            loyalty={loyalty}
            onCompleted={(_data, reference) => {
              if (reference) {
                setPollingReference(reference);
              }
            }}
            onFailed={() => {
              trackPaymentFailed(track, {
                ...analyticsBase,
                failureReason: 'redcat_payment_failed',
              });
              enqueueErrorSnackbar(t('paymentFailedMessage'));
              setPaymentInProgress(false);
              resetPaymentAttempt();
            }}
            onError={error => {
              trackPaymentFailed(track, {
                ...analyticsBase,
                failureReason: 'redcat_payment_error',
              });
              if (goBackOnNewCardError) history.goBack();
              enqueueErrorSnackbar(error?.message || t('paymentFailedMessage'));
              setPaymentInProgress(false);
              resetPaymentAttempt();
            }}
          />
        </RedcatPaymentCardFormStyle>
      </Show>
      <Show when={shouldShowRecaptcha}>
        <div style={styles.recaptchaContainer}>
          <ReCAPTCHA
            sitekey={recaptchaSiteKey!}
            onChange={token => setRecaptchaToken(token)}
            onErrored={() => setRecaptchaToken(null)}
          />
        </div>
      </Show>
      <div style={styles.buttonContainer}>
        <StandardButton
          label={buttonLabel}
          disabled={
            usingSavedCard
              ? savedCardChargeInProgress || (requireRecaptcha && !recaptchaToken)
              : !showCreditCardForm ||
                isPayButtonDisabled({ cardReady, cardValid, paymentInProgress })
          }
          onClick={() => {
            if (usingSavedCard) {
              payWithSavedCard();
            } else if (pollingReference) {
              resumePaymentStatusPolling();
            } else if (submitRedcatPay()) {
              setPaymentInProgress(true);
            }
          }}
          showSpinner={usingSavedCard ? savedCardChargeInProgress : !!paymentInProgress}
          themeKey="loyaltyButton"
          containerStyle={styles.button}
        />
      </div>
    </div>
  );
};

export default RedcatPaySavedCardPaymentForm;

const styles: Styles = {
  mainContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  rightContainer: {
    flex: 1,
  },
  topContainer: {
    paddingTop: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  recaptchaContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 20,
    marginBottom: 16,
  },
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    borderRadius: 5,
    height: 40,
    width: '100%',
  },
  disabled: {
    opacity: 0.5,
    filter: 'grayscale(100%)',
  },
};
